import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import { Box, Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { RuleTemplate, deleteRuleTemplate, listRuleTemplates } from '~/services/PublicOfferFeedService';

import GoogleAdsRuleTemplatesTab from './GoogleAdsRuleTemplatesTab';

function GoogleAdsRuleTemplatesContainer() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<Array<RuleTemplate>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const [totalCount, setTotalCount] = useState(0);
  const { push } = useAppNavigation();
  const [editableTemplate, setEditableTemplate] = useState<RuleTemplate | null>(null);

  // Modal state
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setEditableTemplate(null);
    setOpen(true);
  };

  const handleEditTemplate = (template: RuleTemplate) => {
    setEditableTemplate(template);
    setOpen(true);
  };

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
    });
    push({
      search: `?${searchParams}`,
    });
  }, [push, pageNum, pageSize]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listRuleTemplates({
        page: pageNum,
        limit: pageSize,
      });

      const templates = response.result?.templates || [];
      setData(templates);
      setTotalCount(response.result?.count || 0);
      setQueryParams();
    } catch (error) {
      enqueueSnackbar('Rule Templates fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, setQueryParams, enqueueSnackbar]);

  const handleDeleteTemplate = useCallback(
    async (template: RuleTemplate) => {
      try {
        await deleteRuleTemplate(template.id);
        enqueueSnackbar('Deleted the rule template successfully', { variant: 'success' });
        fetchData();
      } catch (error) {
        enqueueSnackbar(`Failed to delete the rule template: ${error}`, { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchData],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  return (
    <Container sx={{ width: '90%' }}>
      <Helmet>
        <title>Google Ads Rule Templates</title>
      </Helmet>

      <Box>
        <PageSubheader title={`Google Ads Rule Templates [${data?.length} of ${totalCount}]`} />
        <GoogleAdsRuleTemplatesTab
          data={data}
          isLoading={isLoading}
          totalCount={totalCount}
          pageNum={pageNum}
          pageSize={pageSize}
          onPageChange={onPageChange}
          handleOpen={handleOpen}
          handleEditTemplate={handleEditTemplate}
          handleDeleteTemplate={handleDeleteTemplate}
          open={open}
          setOpen={setOpen}
          editableTemplate={editableTemplate}
          fetchData={fetchData}
        />
      </Box>
    </Container>
  );
}

export default GoogleAdsRuleTemplatesContainer;
